import axios from 'axios';

const ADMIN_TOKEN_KEY = 'rh_photo_admin_token';
const ADMIN_USERNAME_KEY = 'rh_photo_admin_username';
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
const DEV_API_ORIGIN = process.env.REACT_APP_DEV_API_ORIGIN || (
  typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.hostname}:8086`
    : 'http://localhost:8086'
);
const API_BASE_URL = IS_DEVELOPMENT ? `${DEV_API_ORIGIN}/api` : '/api';

function shouldUseImageProxy() {
  if (IS_DEVELOPMENT) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

const api = axios.create({
  baseURL: API_BASE_URL
});

export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY) || '';

export const getAdminUsername = () => localStorage.getItem(ADMIN_USERNAME_KEY) || '';

export const saveAdminAuth = ({ token, username }) => {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
  localStorage.setItem(ADMIN_USERNAME_KEY, username);
};

export const clearAdminAuth = () => {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_USERNAME_KEY);
};

api.interceptors.request.use((config) => {
  const token = getAdminToken();

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

function buildProcessedImageUrl(url, options = {}) {
  if (!url) {
    return '';
  }

  const {
    width,
    height,
    mode = 'lfit',
    quality
  } = options;

  if (!width && !height) {
    return url;
  }

  const resizeParts = ['image/resize'];

  if (mode) {
    resizeParts.push(`m_${mode}`);
  }

  if (width) {
    resizeParts.push(`w_${Math.round(width)}`);
  }

  if (height) {
    resizeParts.push(`h_${Math.round(height)}`);
  }

  let processValue = resizeParts.join(',');

  if (quality) {
    processValue += `/quality,q_${Math.round(quality)}`;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}x-oss-process=${encodeURIComponent(processValue)}`;
}

export const proxyImageUrl = (url, options = {}) => {
  if (!url) {
    return '';
  }

  const finalUrl = buildProcessedImageUrl(url, options);

  if (shouldUseImageProxy()) {
    const proxyOrigin = typeof window !== 'undefined'
      ? `${window.location.protocol}//${window.location.host}`
      : DEV_API_ORIGIN;

    return `${proxyOrigin}/api/image-proxy?url=${encodeURIComponent(finalUrl)}`;
  }

  return finalUrl;
};

export const downloadImageFile = async (url, filename = 'image') => {
  const finalUrl = proxyImageUrl(url);
  const response = await fetch(finalUrl, {
    method: 'GET',
    cache: 'no-store'
  });

  if (!response.ok) {
    throw new Error(`下载失败(${response.status})`);
  }

  const blob = await response.blob();

  if (!blob.size) {
    throw new Error('图片内容为空');
  }

  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  const extension = blob.type?.split('/')[1]?.split('+')[0] || 'png';

  link.href = objectUrl;
  link.download = filename.includes('.') ? filename : `${filename}.${extension}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

export const categories = {
  list: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`)
};

export const tags = {
  list: () => api.get('/tags'),
  create: (data) => api.post('/tags', data),
  update: (id, data) => api.put(`/tags/${id}`, data),
  delete: (id) => api.delete(`/tags/${id}`)
};

export const posters = {
  list: (params) => api.get('/posters', { params }),
  search: (params) => api.get('/posters/search', { params }),
  detail: (id) => api.get(`/posters/${id}`),
  create: (data) => api.post('/posters', data),
  batchCreate: (data) => api.post('/posters/batch', data),
  update: (id, data) => api.put(`/posters/${id}`, data),
  delete: (id) => api.delete(`/posters/${id}`)
};

export const upload = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const builtinAssets = {
  list: (params) => api.get('/builtin-assets', { params }),
  create: (data) => api.post('/builtin-assets', data),
  update: (id, data) => api.put(`/builtin-assets/${id}`, data),
  delete: (id) => api.delete(`/builtin-assets/${id}`)
};

export const fontAssets = {
  list: (params) => api.get('/font-assets', { params }),
  create: (data) => api.post('/font-assets', data),
  update: (id, data) => api.put(`/font-assets/${id}`, data),
  delete: (id) => api.delete(`/font-assets/${id}`)
};

export const imageLibrary = {
  list: (params) => api.get('/image-library', { params }),
  create: (data) => api.post('/image-library', data),
  batchCreate: (data) => api.post('/image-library/batch', data),
  update: (id, data) => api.put(`/image-library/${id}`, data),
  delete: (id) => api.delete(`/image-library/${id}`)
};

export const backgroundImages = {
  list: (params) => api.get('/background-images', { params }),
  create: (data) => api.post('/background-images', data),
  batchCreate: (data) => api.post('/background-images/batch', data),
  update: (id, data) => api.put(`/background-images/${id}`, data),
  delete: (id) => api.delete(`/background-images/${id}`)
};

export const materialItems = {
  list: (params) => api.get('/material-items', { params }),
  categories: () => api.get('/material-items/categories'),
  create: (data) => api.post('/material-items', data),
  batchCreate: (data) => api.post('/material-items/batch', data),
  update: (id, data) => api.put(`/material-items/${id}`, data),
  delete: (id) => api.delete(`/material-items/${id}`)
};

export const textStyles = {
  categories: {
    list: () => api.get('/text-styles/categories'),
    create: (data) => api.post('/text-styles/categories', data),
    update: (id, data) => api.put(`/text-styles/categories/${id}`, data),
    delete: (id) => api.delete(`/text-styles/categories/${id}`)
  },
  list: (params) => api.get('/text-styles', { params }),
  create: (data) => api.post('/text-styles', data),
  update: (id, data) => api.put(`/text-styles/${id}`, data),
  delete: (id) => api.delete(`/text-styles/${id}`)
};

export const adminAuth = {
  login: (data) => api.post('/admin-auth/login', data),
  me: () => api.get('/admin-auth/me'),
  changePassword: (data) => api.post('/admin-auth/change-password', data),
  logout: () => api.post('/admin-auth/logout')
};

export default api;
